/**
 * UPA / HOF / Dunk operator console (Phase P6, admin-only, flag-gated).
 *
 * Drives the operator trading/settlement flow over the flag-gated
 * /api/sportsbook-upa/operator/* endpoints: review a game's team-based pricing
 * proposal and window, publish, submit/approve a result (maker/checker), clear
 * integrity, place a hold, and settle. Reuses Shell + app tokens. Mounted only
 * when REACT_APP_UPA_SB_ENABLED is set and only for admin/owner roles.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Shell } from "@/components/shell/Shell";
import api from "@/lib/api";

const EVENT_ID = "evt-hof-1";

function StatusPill({ s }) {
  const cls = {
    ready_for_review: "bg-[#5AADDF24] text-[#5AADDF]",
    operator_only: "bg-[#EAB30824] text-[#EAB308]",
    draft: "bg-[#26262B] text-[#A1A1AA]",
  }[s] || "bg-[#26262B] text-[#A1A1AA]";
  return <span className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase ${cls}`}>{(s || "—").replace(/_/g, " ")}</span>;
}

function Action({ label, onClick, tone = "ghost", busy }) {
  const cls = tone === "primary"
    ? "bg-[#00D632] text-[#0A0A0A]"
    : tone === "danger"
    ? "bg-transparent text-[#EF4444] ring-1 ring-inset ring-[#EF4444]/45"
    : "bg-[#18181C] text-white ring-1 ring-inset ring-[#27272A]";
  return (
    <button onClick={onClick} disabled={busy}
      className={`h-8 rounded-lg px-3 text-[12px] font-bold ${cls} disabled:opacity-50`}>{label}</button>
  );
}

export default function OperatorUPA() {
  const [proposals, setProposals] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const push = (t, ok = true) => setLog((l) => [{ t, ok, at: Date.now() }, ...l].slice(0, 12));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sportsbook-upa/hof/events/${EVENT_ID}/pricing`, { params: { game_number: 1 } });
      setProposals(data.proposals || []);
    } catch (_e) { setProposals([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const call = async (path, body, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sportsbook-upa/operator/${path}`, body);
      push(`${label}: ok`, true);
      return data;
    } catch (e) {
      push(`${label}: ${e?.response?.data?.error || "disabled/err"}`, false);
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <h1 className="mb-1 font-display text-2xl font-extrabold text-white">Pro-Am Basketball — Operator Console</h1>
        <p className="mb-5 text-[13px] text-[#71717A]">
          Team-based pricing review → publish → reviewed result (maker/checker) → integrity clearance → settlement.
          Actions are flag-gated (UPA_SB_PUBLISH_ENABLED) and audit-logged server-side.
        </p>

        <div className="mb-5 overflow-hidden rounded-2xl border border-[#1F1F23] bg-[#0C0C0E]">
          <div className="grid grid-cols-[1.4fr_0.8fr_0.8fr_1fr] gap-2 border-b border-[#1F1F23] bg-[#18181C] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[#71717A]">
            <span>Game</span><span>Fair / Offered</span><span>Confidence</span><span>Proposal</span>
          </div>
          {proposals.length === 0 && (
            <div className="px-4 py-6 text-[13px] text-[#71717A]">No priced proposals (feed/flags off in this environment).</div>
          )}
          {proposals.map((p, i) => {
            const pr = p.proposal;
            const cgid = pr?.canonical_game_id || p.match_id;
            return (
              <div key={i} className="border-b border-[#1F1F23] px-4 py-3">
                <div className="grid grid-cols-[1.4fr_0.8fr_0.8fr_1fr] items-center gap-2">
                  <span className="font-mono text-[11px] text-[#A1A1AA]">{p.match_id}</span>
                  <span className="font-display text-[13px] font-extrabold tabular-nums text-white">
                    {pr ? `${pr.team1_price > 0 ? "+" : ""}${pr.team1_price} / ${pr.team2_price > 0 ? "+" : ""}${pr.team2_price}` : "—"}
                  </span>
                  <span className="text-[13px] text-[#E4E4E7]">{pr ? pr.confidence : "—"}</span>
                  <StatusPill s={pr?.status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Action label="Publish" tone="primary" busy={busy}
                    onClick={() => call("publish", { canonical_game_id: cgid, approver: "operator" }, "publish")} />
                  <Action label="Submit result (T1)" busy={busy}
                    onClick={() => call("submit-result", { canonical_game_id: cgid, winner_team_id: pr?.team1_id, submitted_by: "ops_a" }, "submit")} />
                  <Action label="Approve" busy={busy}
                    onClick={() => call("approve-result", { canonical_game_id: cgid, approver: "ops_b" }, "approve")} />
                  <Action label="Clear integrity" busy={busy}
                    onClick={() => call("clear-integrity", { canonical_game_id: cgid, cleared_by: "integrity" }, "clear")} />
                  <Action label="Hold" tone="danger" busy={busy}
                    onClick={() => call("hold", { canonical_game_id: cgid }, "hold")} />
                  <Action label="Settle" tone="primary" busy={busy}
                    onClick={() => call("settle", { canonical_game_id: cgid }, "settle")} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-4">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#71717A]">Action log</div>
          {log.length === 0 ? (
            <div className="text-[13px] text-[#71717A]">No actions yet.</div>
          ) : (
            <div className="grid gap-1 font-mono text-[12px]">
              {log.map((l, i) => (
                <div key={i} className={l.ok ? "text-[#00D632]" : "text-[#EAB308]"}>{l.t}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
